import React from "react";
import { 
  User, 
  Globe, 
  FileText, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock,
  Archive,
  X
} from "lucide-react";
import { toggleArchivePartenaire } from "../api/partenairesAPI";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../../components/ui/button";

export default function PartenaireDetail({ partenaire, onClose, onEdit, refreshData }) {
  if (!partenaire) return null;

  const statutConfig = {
    "Actif": { icon: <CheckCircle className="h-6 w-6 text-[var(--hp-success-700)]" />, bgColor: "bg-[var(--hp-success-100)] text-[var(--hp-success-800)]" },
    "Inactif": { icon: <XCircle className="h-6 w-6 text-[var(--hp-danger-700)]" />, bgColor: "bg-[var(--hp-danger-100)] text-[var(--hp-danger-800)]" },
    "Onboarding": { icon: <Clock className="h-6 w-6 text-[var(--hp-warning-700)]" />, bgColor: "bg-[var(--hp-warning-100)] text-[var(--hp-warning-800)]" },
    "Non renseigné": { icon: <Clock className="h-6 w-6 text-subtle" />, bgColor: "bg-surface-muted text-primary" }
  };

  const statut = partenaire.statutConformite || "Non renseigné";
  const { icon, bgColor } = statutConfig[statut] || statutConfig["Non renseigné"];

  const handleArchive = async () => {
    await toggleArchivePartenaire(partenaire.id, partenaire.archived);
    refreshData();
    onClose(); 
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[999] flex justify-end">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Slide-over panel */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="relative w-full max-w-md h-full bg-surface shadow-2xl flex flex-col border-l border-[var(--hp-color-border-muted)]"
        >
          {/* En-tête du slide-over */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md">
            <h2 className="text-xl font-bold text-primary">Dossier Partenaire</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5 text-subtle hover:text-primary" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {/* Profil principal */}
            <div className="flex flex-col items-center mb-8">
              {partenaire.logo ? (
                <img
                  src={partenaire.logo}
                  alt="Logo"
                  className="h-24 w-24 object-contain rounded-xl shadow-hp-sm mb-4 border border-[var(--hp-color-border-muted)] bg-white p-2"
                />
              ) : (
                <div className="h-24 w-24 bg-surface-muted rounded-full flex items-center justify-center border border-[var(--hp-color-border-muted)] shadow-hp-sm mb-4">
                  <User className="h-12 w-12 text-subtle" />
                </div>
              )}
              <h2 className="text-2xl font-bold text-primary text-center">{partenaire.nom_societe}</h2>
              <p className="text-sm text-subtle mt-1">Ajouté le : {new Date(partenaire.dateAjout || Date.now()).toLocaleDateString("fr-FR")}</p>
              
              <div className={`mt-4 flex items-center justify-center px-4 py-2 rounded-full shadow-hp-sm border border-[var(--hp-color-border-muted)] ${bgColor}`}>
                {icon}
                <span className="ml-2 font-medium">Statut : {statut}</span>
              </div>
            </div>

            {/* Sections de détails */}
            <div className="space-y-6">
              {/* Informations principales */}
              <div className="workspace-card-elevated p-5">
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-secondary" />
                  Informations
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Type de structure</p>
                    <p className="font-medium text-primary">{partenaire.type_structure || "Non renseigné"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Classe d'actifs</p>
                    <p className="font-medium text-primary">{partenaire.classe_actifs || "Non renseigné"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Numéro d'enregistrement</p>
                    <p className="font-medium text-primary">{partenaire.numeroEnregistrement || "Non renseigné"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Volume d'affaires</p>
                    <p className="font-medium text-[var(--hp-success-700)] text-lg">
                      {partenaire.volumeAffaires ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(partenaire.volumeAffaires) : "0,00 €"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="workspace-card-elevated p-5">
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-secondary" />
                  Contact
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Référent</p>
                    <p className="font-medium text-primary">{partenaire.nom_referent || "Non renseigné"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Email</p>
                    <a href={`mailto:${partenaire.email}`} className="font-medium text-secondary hover:underline">{partenaire.email || "Non renseigné"}</a>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Téléphone</p>
                    <p className="font-medium text-primary">{partenaire.telephone || "Non renseigné"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-subtle uppercase tracking-wide">Adresse</p>
                    <p className="font-medium text-primary">{partenaire.adresse || "Non renseigné"}</p>
                  </div>
                  {partenaire.siteWeb && (
                    <div className="pt-2">
                      <a 
                        href={partenaire.siteWeb.startsWith('http') ? partenaire.siteWeb : `https://${partenaire.siteWeb}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-[var(--hp-color-accent-active)] transition-colors"
                      >
                        <Globe className="h-4 w-4" />
                        Visiter le site web
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Description & Commentaires */}
              {(partenaire.description || (partenaire.documentsRequis && partenaire.documentsRequis.length > 0) || partenaire.commentairesInternes) && (
                <div className="workspace-card-elevated p-5 space-y-4">
                  {partenaire.description && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-subtle mb-1 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Description
                      </h3>
                      <p className="text-sm text-primary">{partenaire.description}</p>
                    </div>
                  )}
                  
                  {partenaire.documentsRequis && partenaire.documentsRequis.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-subtle mb-1 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Documents requis
                      </h3>
                      <ul className="text-sm text-primary list-disc pl-5">
                        {partenaire.documentsRequis.map((doc, index) => (
                          <li key={index}>{doc}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {partenaire.commentairesInternes && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-subtle mb-1 flex items-center gap-2">
                        <MessageSquare className="w-3.5 h-3.5" /> Notes internes
                      </h3>
                      <p className="text-sm text-primary p-3 bg-surface-muted rounded-md border border-[var(--hp-color-border-muted)]">{partenaire.commentairesInternes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer avec boutons d'action */}
          <div className="p-4 bg-workspace border-t border-[var(--hp-color-border-muted)] space-y-3">
            <Button 
              variant="primary"
              className="w-full justify-center"
              onClick={() => onEdit(partenaire)}
            >
              Modifier le dossier
            </Button>
            
            <div className="flex gap-3">
              <Button 
                variant="secondary"
                className="flex-1 justify-center border-dashed border-[var(--hp-color-border-strong)]"
                onClick={handleArchive}
              >
                <Archive className="h-4 w-4 mr-2" />
                {partenaire.archived ? "Désarchiver" : "Archiver"}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
